import styled, { css } from 'styled-components';
import { sdsColors, sdsFontFamily, sdsGrayscale, sdsMessageBg } from '../../../theme/sds-tokens';
import { chartCardSurfaceDark, chartSubtitleColor, chartTitleColor } from './chartColors';

/* Modo claro vem dos tokens do SDS; o modo escuro usa a paleta definida para
   os cards de absenteismo, que nao tem equivalente no SDS. As cores descem
   para os filhos como custom properties — so o card precisa saber o modo. */
export const Card = styled.section<{ $isDark: boolean }>`
  --abs-surface: ${({ $isDark }) => ($isDark ? chartCardSurfaceDark : sdsGrayscale[0])};
  --abs-border: ${({ $isDark }) => ($isDark ? '#202528' : sdsGrayscale[20])};
  /* Titulo e subtitulo nao mudam com o modo: e a cor que os outros graficos do
     dashboard usam, e a tela precisa ter titulo de uma cor so. */
  --abs-title: ${chartTitleColor};
  --abs-subtitle: ${chartSubtitleColor};
  --abs-label: ${({ $isDark }) => ($isDark ? '#D5DBDD' : sdsColors.textPrimary)};
  --abs-track: ${({ $isDark }) => ($isDark ? '#2A2E31' : sdsGrayscale[10])};
  --abs-icon: ${sdsColors.primary};
  --abs-header-bg: ${({ $isDark }) => ($isDark ? '#202528' : sdsGrayscale[5])};
  --abs-icon-bg: ${({ $isDark }) => ($isDark ? '#2A2E31' : sdsMessageBg.info)};

  box-sizing: border-box;
  width: 100%;
  height: 100%;
  background: var(--abs-surface);
  border: 1px solid var(--abs-border);
  border-radius: 16px;
  padding: 16px 16px;
  font-family: ${sdsFontFamily};

  @media (max-width: 480px) {
    padding: 18px 16px;
  }
`;

export const CardHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin: -16px -16px 0;
  padding: 16px;
  border-bottom: 1px solid var(--abs-border);
  border-radius: 15px 15px 0 0;
  background: var(--abs-header-bg);

  @media (max-width: 480px) {
    margin-top: -18px;
    padding-top: 18px;
  }
`;

export const CardIcon = styled.span`
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background: var(--abs-icon-bg);
  color: var(--abs-icon);

  svg {
    font-size: 22px;
  }
`;

export const CardHeading = styled.div`
  min-width: 0;
`;

export const CardTitle = styled.h3`
  margin: 0;
  font-family: inherit;
  font-size: 16px;
  font-weight: 700;
  line-height: 1.35;
  color: var(--abs-title);
`;

export const CardSubtitle = styled.p`
  margin: 4px 0 0;
  font-family: inherit;
  font-size: 12px;
  font-weight: 400;
  line-height: 1.45;
  color: var(--abs-subtitle);
`;

export const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-top: 20px;
`;

export const Row = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
`;

export const ItemLabel = styled.span`
  font-family: inherit;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.35;
  color: var(--abs-label);
  overflow-wrap: anywhere;
`;

export const Track = styled.div`
  position: relative;
  box-sizing: border-box;
  width: 100%;
  height: 41px;
  border-radius: 11px;
  background: var(--abs-track);
  overflow: hidden;

  @media (max-width: 480px) {
    height: 40px;
  }
`;

/* As duas camadas ocupam a trilha inteira e repetem o mesmo conteudo: a de
   baixo pinta o valor sobre a trilha, a de cima e recortada por clip-path na
   largura real do percentual. Assim a barra reflete o percentual exato — 0%
   nao mostra preenchimento nenhum — e o texto troca de cor exatamente na
   borda da barra, sem depender da largura do rotulo. */
const layer = css`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
`;

export const ValueLayer = styled.div`
  ${layer};
  color: var(--abs-label);
`;

export const Fill = styled.div`
  ${layer};
`;

export const Value = styled.div`
  display: flex;
  align-items: baseline;
  gap: 8px;
  padding: 0 14px;
  white-space: nowrap;
  color: inherit;
`;

export const Percentage = styled.span`
  font-family: inherit;
  font-size: 22px;
  font-weight: 700;
  line-height: 1;

  @media (max-width: 480px) {
    font-size: 19px;
  }
`;

export const PercentageSign = styled.span`
  font-family: inherit;
  font-size: 12px;
  font-weight: 600;
  vertical-align: super;
`;

export const Amount = styled.span`
  font-family: inherit;
  font-size: 11px;
  font-weight: 700;
  line-height: 1;
  color: inherit;
`;
